import React, { useCallback, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import logoImg from "../../assets/logo.png";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { Form } from "../../components/form/Form";
import { useToastr } from "../../components/toast/Toast";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IOption } from "../../interfaces/IOption";
import { IState } from "../../interfaces/IState";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";
import { Container } from "./SignUp.styles";

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const toastr = useToastr();

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [address, setAddress] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [addressReference, setAddressReference] = useState("");
  const [association, setAssociation] = useState("");

  const [statesOptions, setStatesOptions] = useState<IOption[]>([]);
  const [stateOption, setStateOption] = useState<IOption | null>(null);

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErros] = useState<IFormErrors>({});

  const stateToOption = useCallback(
    (state: IState): IOption => ({
      value: `${state.id}`,
      description: `${state.id} - ${state.name}`,
    }),
    [],
  );

  const handleSetState = (value: string | null) => {
    if (value) {
      const selected = statesOptions.find(s => s.value === value);
      setStateOption(selected || null);
    }
  };

  const handleListStates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/states");
      const options = data.map(stateToOption);
      setStatesOptions(options);
    } catch (error: any) {
      toastr.error(
        error.response?.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [toastr, stateToOption]);

  const handleRequestZipCode = useCallback(async () => {
    if (zipCode.length === 9) {
      setLoading(true);
      try {
        const { data } = await api.get(`/cep/${zipCode}`);
        setCountry("Brasil");
        setCity(data.localidade || "");
        setNeighborhood(data.bairro || "");
        setAddress(data.logradouro || "");
        setAddressNumber("");
        setAddressComplement("");
        setAddressReference("");
        const state = statesOptions.find(
          s => s.value.toUpperCase() === (data.uf || "").toUpperCase(),
        );
        setStateOption(state || null);
      } catch (error: any) {
        toastr.error(
          error.response?.data?.message || "Contate a equipe de suporte",
        );
      } finally {
        setLoading(false);
      }
    }
  }, [zipCode, statesOptions, toastr]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    setLoading(true);
    setFormErros({});

    try {
      const data = {
        name,
        username,
        email,
        zipCode,
        country,
        stateId: stateOption?.value || "",
        city,
        neighborhood,
        address,
        addressNumber,
        addressComplement,
        addressReference,
        association,
      };

      const schema = Yup.object().shape({
        name: Yup.string().required("Nome é obrigatórios"),
        username: Yup.string().required("Nome de usuário é obrigatórios"),
        email: Yup.string().email("Formato inválido"),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      await api
        .post(`/collectors`, data)
        .then(response => {
          toastr.success("Usuário cadastrado com sucesso");
          navigate(`/collector/${response.data.id}`);
        })
        .catch(error => {
          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (err: Yup.ValidationError | any) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationError(err);
        setFormErros(errors);
        return;
      }

      toastr.error("Ocorreu um erro ao fazer login, cheque as credênciais.");
    } finally {
      setLoading(false);
    }
  }, [
    name,
    username,
    email,
    zipCode,
    country,
    stateOption,
    city,
    neighborhood,
    address,
    addressNumber,
    addressComplement,
    addressReference,
    association,
    navigate,
    toastr,
  ]);

  useEffect(() => {
    handleListStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <img src={logoImg} alt="" />
      <h1>Cadastre-se como colecionador para ter acesso ao sistema</h1>

      <Form>
        <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="Nome"
              value={name}
              required
              autoFocus
              onChange={e => setName(e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="Usuário"
              value={username}
              required
              onChange={e =>
                setUsername(String(e.target.value).toLowerCase().trim())
              }
              error={!!formErrors.username}
              helperText={formErrors.username}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="E-mail"
              value={email}
              onChange={e => setEmail(e.target.value.toLowerCase().trim())}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <InputMask
              mask={"99999-999"}
              value={zipCode}
              onChange={e => setZipCode(e.target.value.trim())}
              maskPlaceholder={null}
              onBlur={handleRequestZipCode}
            >
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="CEP"
                helperText={formErrors.zipCode}
                error={!!formErrors.zipCode}
              />
            </InputMask>
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="País"
              value={country}
              onChange={e => setCountry(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <FormControl fullWidth size="small" error={!!formErrors.stateId}>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={stateOption?.value || ""}
                onChange={e => handleSetState(String(e.target.value))}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 5.5, // 5 itens visíveis (aprox. 48px cada)
                    },
                  },
                }}
              >
                {statesOptions.map(s => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.description}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{formErrors.stateId}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="Cidade"
              value={city}
              onChange={e => setCity(e.target.value)}
              error={!!formErrors.city}
              helperText={formErrors.city}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="Bairro"
              value={neighborhood}
              onChange={e => setNeighborhood(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="Endereço"
              value={address}
              onChange={e => setAddress(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="Número"
              value={addressNumber}
              onChange={e => setAddressNumber(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="Complemento"
              value={addressComplement}
              onChange={e => setAddressComplement(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="Referência"
              value={addressReference}
              onChange={e => setAddressReference(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: "100%" }}
              size="small"
              label="Associação"
              value={association}
              onChange={e => setAssociation(e.target.value)}
            />
          </Grid>
        </Grid>

        <ButtonGroup
          justformobilie
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <ButtonTheme
            onClick={() => navigate("/users")}
            variant="contained"
            color="inherit"
            disabled={loading}
            style={{ width: "100%" }}
          >
            Voltar
          </ButtonTheme>

          <ButtonTheme
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            style={{ width: "100%" }}
          >
            Confirmar
          </ButtonTheme>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export { SignUp };
